import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { RenderingErrorBoundaryChildProps } from 'components/RenderingErrorBoundary';
import AIButton from 'components/shared/ai/AIButton';
import Explainer from 'components/shared/ai/Explainer';
import SourceChip from 'components/shared/ai/SourceChip';
import { getReturnUrlPath } from 'components/shared/ai/summary/lib';
import TimingExplainer from 'components/shared/ai/TimingExplainer';
import { useEpisode } from 'context/episode';
import { useTrackAIJob } from 'hooks/useTrackAIJob';
import { simpleDate } from 'lib/date';
import AIJob, { AIJobOptions } from 'models/AIJob';
import Authorization, { AuthorizationOptions } from 'models/reviews/Authorization';
import { createAIJob } from 'services/api/aiJobs';
import { authorizationQueryKeys } from 'services/api/reviews';
import { queryClient } from 'services/http';
import { AIJobNotificationType } from 'stores/aiJobNotificationStore';
import AIOlioLoader from 'svg/AIOlioLoader';

import { DownloadAIExportButton } from '../../shared/ai/DownloadAIExportButton';

import ComparisonEmptyState from './comparison/ComparisonEmptyState';
import ComparisonSection from './comparison/ComparisonSection';

type ConcurrentReviewComparisonTabProps = RenderingErrorBoundaryChildProps & {
  authorization: Authorization;
  pendingActions?: Array<'summarize' | 'compare'>;
  isSummarized: boolean;
};

export default function ConcurrentReviewComparisonTab({
  authorization,
  pendingActions = [],
  isSummarized,
  isErrored = false,
  resetErrorBoundary,
}: ConcurrentReviewComparisonTabProps) {
  const location = useLocation();
  const { startTracking, isTracking } = useTrackAIJob();
  const { episode } = useEpisode();
  const { comparison, comparisonJob } = authorization;
  const comparisonJobInProgress = comparisonJob && comparisonJob.isProcessing && !comparison;
  const approvalThroughDate = simpleDate(authorization.approvedThroughDate) ?? undefined;
  const lastUpdatedDate = simpleDate(authorization.previousAuthorizationReview?.approvedThroughDate) ?? undefined;

  const [isCreatingJob, setIsCreatingJob] = useState(false);

  const updateAuthorizationComparisonJobCache = useCallback(
    (job: AIJob) => {
      queryClient.setQueryData(
        authorizationQueryKeys.show({ id: authorization.id }),
        (authorization: Authorization) => {
          return new Authorization({
            ...authorization,
            comparisonJobForCurrentSummary: job,
          } as AuthorizationOptions);
        }
      );
    },
    [authorization.id]
  );

  const handleCollapsePromptExplainer = useCallback(() => {
    updateAuthorizationComparisonJobCache(
      new AIJob({
        ...comparisonJob,
        hasCollapsedPromptExplainer: true,
      } as AIJobOptions)
    );
  }, [comparisonJob, updateAuthorizationComparisonJobCache]);

  const handleCompareSummaries = useCallback(async () => {
    resetErrorBoundary?.();
    setIsCreatingJob(true);
    try {
      const newJob = await createAIJob({
        type: 'comparison',
        reviewId: authorization?.id,
      });
      updateAuthorizationComparisonJobCache(newJob);
    } catch {
      setIsCreatingJob(false);
    }
  }, [authorization?.id, resetErrorBoundary, updateAuthorizationComparisonJobCache]);

  // Check if we need to track on page load and on a job update
  useEffect(() => {
    if (comparisonJobInProgress && !isTracking(comparisonJob.id)) {
      startTracking({
        jobId: comparisonJob.id,
        jobType: AIJobNotificationType.AUTHORIZATION_COMPARISON,
        jobbableId: authorization.id,
        patientName: episode?.patient?.name,
        returnUrl: getReturnUrlPath(location.pathname, 'comparison'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonJobInProgress]);

  // Handle pending actions from other tabs
  useEffect(() => {
    const compareIndex = pendingActions.findIndex((action) => action === 'compare');
    if (compareIndex !== -1 && isSummarized && !comparisonJob && !isCreatingJob) {
      pendingActions.splice(compareIndex, 1);
      handleCompareSummaries();
    }
  }, [handleCompareSummaries, pendingActions, isSummarized, comparisonJob, isCreatingJob]);

  const allAttachments = useMemo(() => {
    const primarySources = comparison?.data?.primary?.sources ?? [];
    const secondarySources = comparison?.data?.secondary?.sources ?? [];

    // Explicitly check `sources` length before attempting to spread the array,
    // just in case the AI platform incorrectly returned an object instead of an array.
    // Otherwise it won't reach the logic to render the errored state.
    return primarySources.length && secondarySources.length ? [...primarySources, ...secondarySources] : [];
  }, [comparison?.data?.primary?.sources, comparison?.data?.secondary?.sources]);

  const findAttachments = (attachmentIds: string[]) =>
    attachmentIds.flatMap((x) => allAttachments.find(({ attachmentId }) => attachmentId === x) || []);

  useEffect(() => {
    if (isCreatingJob && comparisonJobInProgress) setIsCreatingJob(false);
  }, [comparisonJobInProgress, isCreatingJob]);

  if (isCreatingJob || comparisonJobInProgress) {
    return (
      <LoaderContainer>
        <SpinnerContainer>
          <AIOlioLoader height={120} />
          <span style={{ marginTop: -6 }}>Comparing summaries...</span>
        </SpinnerContainer>
        <TimingExplainer jobType='comparison' />
      </LoaderContainer>
    );
  }

  if (isErrored || comparisonJob?.isErrored) {
    return (
      <ErrorContainer>
        <span>Comparison failed to process. Click below to try again.</span>
        <div style={{ width: 148 }}>
          <AIButton onClick={handleCompareSummaries} fullWidth>
            Try Again
          </AIButton>
        </div>
      </ErrorContainer>
    );
  }

  if (isSummarized && comparison) {
    return (
      <ComparedContainer>
        <MetadataSpan>
          Comparison created by {comparisonJob.createdBy?.fullName || 'Unknown'}
          {comparisonJob.createdByGroup?.name && ` at ${comparisonJob.createdByGroup.name}`} on{' '}
          {comparisonJob?.createdAt ? simpleDate(comparisonJob.createdAt) : 'Unknown date'}
        </MetadataSpan>
        <ActionContainer>
          <DownloadAIExportButton artifactId={comparison.id} artifactType='comparison' />
        </ActionContainer>
        <Explainer
          title={`Comparing to ${lastUpdatedDate ?? 'N/A'}`}
          aiPrompt={comparison.aiPrompt ?? undefined}
          hasBeenCollapsed={comparisonJob?.hasCollapsedPromptExplainer ?? undefined}
          onCollapseSuccess={handleCollapsePromptExplainer}
        />
        <SourcesContainer>
          <SourcesTitle>
            {approvalThroughDate ?? 'N/A'} Sources ({comparison.data.primary.sources.length})
          </SourcesTitle>
          <CitationsContainer>
            {comparison.data.primary.sources.map((attachment) => (
              <SourceChip key={`new-${attachment.attachmentId}`} attachment={attachment} />
            ))}
          </CitationsContainer>
        </SourcesContainer>
        <Divider />
        <SourcesContainer>
          <SourcesTitle>
            {lastUpdatedDate ?? 'N/A'} Sources ({comparison.data.secondary.sources.length})
          </SourcesTitle>
          <CitationsContainer>
            {comparison.data.secondary.sources.map((attachment) => (
              <SourceChip key={`old-${attachment.attachmentId}`} attachment={attachment} />
            ))}
          </CitationsContainer>
        </SourcesContainer>
        <ContentContainer>
          {comparison.data.sections.map(({ title, attachmentIds, content }, idx) => (
            <ComparisonSection
              key={`comparison-section-${idx}`}
              title={title}
              attachments={findAttachments(attachmentIds)}
              content={content}
            />
          ))}
        </ContentContainer>
      </ComparedContainer>
    );
  }

  return (
    <>
      <ComparisonEmptyState
        approvalThroughDate={approvalThroughDate}
        lastUpdatedDate={lastUpdatedDate}
        onCompareSummaries={handleCompareSummaries}
        isSummarized={isSummarized}
      />
    </>
  );
}

const Divider = styled.div`
  border-bottom: 1px solid var(--black-10);
`;

const MetadataSpan = styled.span`
  font-style: italic;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0.23px;
`;

const LoaderContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

const SpinnerContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 2%;
`;

const ComparedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const SourcesContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
`;

const SourcesTitle = styled.h2`
  font-weight: 700;
  font-size: 14px;
  line-height: 12px;
  letter-spacing: 0.2px;
`;

const CitationsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  height: 100%;

  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 2%;
  text-align: center;
`;
